import React, { useEffect, useState } from "react";
import { ShieldAlert, Phone, Mail, Link as LinkIcon, Globe, Loader2, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

const KIND_ICONS = { phone: Phone, email: Mail, url: LinkIcon, domain: Globe };
const KIND_COLORS = {
  phone:  "bg-amber-100 text-amber-800",
  email:  "bg-blue-100 text-blue-800",
  url:    "bg-purple-100 text-purple-800",
  domain: "bg-emerald-100 text-emerald-800",
};

export default function PoachingAudit() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get(`/admin/poaching/audit?limit=200${filterRole ? `&role=${filterRole}` : ""}`),
        api.get("/admin/poaching/stats"),
      ]);
      setItems(a.data);
      setStats(s.data);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, s] = await Promise.all([
          api.get(`/admin/poaching/audit?limit=200${filterRole ? `&role=${filterRole}` : ""}`),
          api.get("/admin/poaching/stats"),
        ]);
        if (cancelled) return;
        setItems(a.data);
        setStats(s.data);
      } catch { /* noop */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [filterRole]);

  return (
    <div className="space-y-4" data-testid="poaching-audit-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
            Anti-Poaching Audit
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Customers and designers who attempted to share contact info (phone/email/URLs/domains) in chat. Admin sees raw; designers + customers see masked.</p>
        </div>
        <button onClick={load} data-testid="audit-refresh" className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a3c5e] text-white text-xs font-semibold rounded-lg hover:bg-[#122a42]">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatPill label="Total Hits" value={stats.total} tone="amber" testId="audit-stat-total" />
          <StatPill label="By Customer" value={stats.by_role.customer || 0} tone="blue" testId="audit-stat-customer" />
          <StatPill label="By Designer" value={stats.by_role.designer || 0} tone="red" testId="audit-stat-designer" />
          {Object.entries(stats.by_kind).map(([kind, count]) => {
            const Icon = KIND_ICONS[kind] || ShieldAlert;
            return (
              <div key={kind} className={`rounded-xl border border-[#1a3c5e]/10 bg-white p-3 flex items-center gap-2`} data-testid={`audit-kind-${kind}`}>
                <Icon className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{kind}</div>
                  <div className="font-display font-bold text-[#1a3c5e]">{count}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-[#1a3c5e]">Filter:</span>
        {[
          { v: "", l: "All" },
          { v: "customer", l: "Customers only" },
          { v: "designer", l: "Designers only" },
        ].map(({ v, l }) => (
          <button
            key={v || "all"}
            onClick={() => setFilterRole(v)}
            data-testid={`audit-filter-${v || "all"}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold ${filterRole === v ? "bg-[#1a3c5e] text-white" : "bg-white border border-[#1a3c5e]/15 text-[#1a3c5e]"}`}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#F7941D] mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-gray-400 text-sm bg-white rounded-2xl border border-[#1a3c5e]/10">
          No contact-sharing attempts detected. {filterRole && "(filter active)"}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[#FEF3E2]/40 text-[#1a3c5e]">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">When</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Who</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Kinds</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Raw excerpt (admin-only)</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{new Date(it.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-3">
                      <div className={`font-semibold text-xs uppercase ${it.sender_role === "designer" ? "text-red-600" : "text-blue-600"}`}>{it.sender_role}</div>
                      <div className="text-xs text-[#1a3c5e]">{it.sender_name || "—"}</div>
                      <div className="text-[11px] text-gray-500">{it.sender_email || it.sender_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {it.kinds.map(k => {
                          const Icon = KIND_ICONS[k] || ShieldAlert;
                          return (
                            <span key={k} className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 flex items-center gap-0.5 ${KIND_COLORS[k] || "bg-gray-100 text-gray-700"}`}>
                              <Icon className="w-2.5 h-2.5" /> {k}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-mono break-words max-w-md">{it.raw_excerpt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const TONES = {
  amber: { bg: "bg-amber-50",  border: "border-amber-200",  txt: "text-amber-700" },
  blue:  { bg: "bg-blue-50",   border: "border-blue-200",   txt: "text-blue-700" },
  red:   { bg: "bg-red-50",    border: "border-red-200",    txt: "text-red-700" },
};
function StatPill({ label, value, tone = "amber", testId }) {
  const t = TONES[tone] || TONES.amber;
  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} p-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
      <div className={`font-display font-extrabold text-2xl ${t.txt}`}>{value}</div>
    </div>
  );
}
